import { useEffect, useState } from 'react';

import RefreshIcon from '../icons/RefreshIcon';

function formatRelativeTime(date) {
  if (!date) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  return `${diffHour}h ago`;
}

/**
 * Control bar presenting manual refresh action and live sync status.
 *
 * @param {Object} props
 * @param {boolean} props.isRefreshing - Whether a sync operation is currently active
 * @param {Date|null} props.lastRefreshedAt - Timestamp of the last successful refresh/sync
 * @param {boolean} [props.disabled=false] - Whether manual refresh is disabled
 * @param {function} props.onRefresh - Callback to trigger sync
 */
export default function PhotoSyncBar({
  isRefreshing,
  lastRefreshedAt,
  disabled = false,
  onRefresh,
}) {
  // Periodically tick every 10s to keep relative time text ("Updated 15s ago") fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
      <button
        type="button"
        onClick={onRefresh}
        disabled={disabled || isRefreshing}
        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-full bg-white/80 hover:bg-white active:scale-95 text-rose-600 shadow-sm border border-rose-200/80 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        title="Refresh photos"
        aria-label="Refresh photos"
      >
        <RefreshIcon
          className={`w-3.5 h-3.5 text-rose-500 transition-transform ${
            isRefreshing ? 'animate-spin' : ''
          }`}
        />
        <span>{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
      </button>

      <div className="flex items-center gap-1.5 text-xs text-rose-400/90 font-medium select-none">
        {isRefreshing ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
            </span>
            <span>Syncing photos…</span>
          </>
        ) : lastRefreshedAt ? (
          <>
            <span className="inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            <span>Updated {formatRelativeTime(lastRefreshedAt)}</span>
          </>
        ) : (
          <span>Auto-updates active</span>
        )}
      </div>
    </div>
  );
}
